import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { 
  Box, 
  Container, 
  TextField, 
  Typography, 
  CircularProgress,
  Paper,
  Avatar
} from "@mui/material";
import { Button } from "reactstrap";
import { FormattedMessage } from "react-intl";
import LockResetIcon from "@mui/icons-material/LockReset";
import { verifyResetToken, resetPassword, setShowLogin } from "../Redux/user/slice";

export function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isFetching } = useSelector((state) => state.user);
  
  const [isVerifying, setIsVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState("");
  const [email, setEmail] = useState("");
  
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmError, setConfirmError] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  // Verify token on mount
  useEffect(() => {
    if (token) {
      dispatch(verifyResetToken({
        token,
        onSuccess: (response) => {
          setIsVerifying(false);
          setTokenValid(true);
          setEmail(response.email);
        },
        onError: (error) => {
          setIsVerifying(false);
          setTokenValid(false);
          setTokenError(error);
        }
      }));
    }
  }, [token, dispatch]);

  const validatePassword = (password) => {
    if (!password) {
      return <FormattedMessage id="resetpassword.error.password.required" />;
    }
    if (password.length < 6) {
      return <FormattedMessage id="resetpassword.error.password.length" />;
    }
    return "";
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setPasswordError("");
    setConfirmError("");
    
    // Validate password
    const passwordValidation = validatePassword(newPassword);
    if (passwordValidation) {
      setPasswordError(passwordValidation);
      return;
    }
    
    // Check if passwords match
    if (newPassword !== confirmPassword) {
      setConfirmError(<FormattedMessage id="resetpassword.error.password.mismatch" />);
      return;
    }
    
    // Dispatch reset password action
    dispatch(resetPassword({
      token,
      newPassword,
      onSuccess: () => {
        setIsSuccess(true);
        // Redirect to home page after 3 seconds
        setTimeout(() => {
          navigate("/");
          dispatch(setShowLogin({ value: true, cb: null }));
        }, 3000);
      },
      onError: (error) => {
        setPasswordError(error);
      }
    }));
  };

  const handleBackToLogin = () => {
    navigate("/");
    dispatch(setShowLogin({ value: true, cb: null }));
  };

  // Verifying token state
  if (isVerifying) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 3 }}>
            <FormattedMessage id="resetpassword.verifying" />
          </Typography>
        </Box>
      </Container>
    );
  }

  // Invalid token state
  if (!tokenValid) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Paper elevation={3} sx={{ p: 4, width: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 3 }}>
              <Avatar sx={{ m: 1, bgcolor: "error.main" }}>
                <LockResetIcon />
              </Avatar>
              <Typography component="h1" variant="h5" color="error">
                <FormattedMessage id="resetpassword.error.token.invalid" />
              </Typography>
            </Box>
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: "center", mb: 3 }}>
              {tokenError}
            </Typography>
            <Button 
              color="primary"
              onClick={handleBackToLogin}
              style={{ width: "100%" }}
            >
              <FormattedMessage id="forgotpassword.button.backtologin" />
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  // Success state
  if (isSuccess) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Paper elevation={3} sx={{ p: 4, width: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 3 }}>
              <Avatar sx={{ m: 1, bgcolor: "success.main" }}>
                <LockResetIcon />
              </Avatar>
              <Typography component="h1" variant="h5" color="success.main">
                <FormattedMessage id="resetpassword.success.message" />
              </Typography>
            </Box>
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: "center", mb: 3 }}>
              <FormattedMessage id="resetpassword.redirecting" />
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <CircularProgress size={40} />
            </Box>
          </Paper>
        </Box>
      </Container>
    );
  }

  // Reset password form
  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: "100%" }}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 3 }}>
            <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
              <LockResetIcon />
            </Avatar>
            <Typography component="h1" variant="h5">
              <FormattedMessage id="resetpassword.page.title" />
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mb: 3 }}>
            <FormattedMessage id="resetpassword.page.description" />
          </Typography>

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              name="newPassword"
              label={<FormattedMessage id="resetpassword.password.label" />}
              type="password"
              id="newPassword"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                setPasswordError("");
              }}
              error={passwordError !== ""}
              helperText={passwordError}
              disabled={isFetching}
              autoFocus
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label={<FormattedMessage id="resetpassword.confirm.label" />}
              type="password"
              id="confirmPassword"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setConfirmError("");
              }}
              error={confirmError !== ""}
              helperText={confirmError}
              disabled={isFetching}
            />

            <Button
              type="submit"
              color="primary"
              disabled={isFetching}
              style={{ width: "100%", marginTop: "24px" }}
            >
              {isFetching ? (
                <CircularProgress size={24} />
              ) : (
                <FormattedMessage id="resetpassword.button.reset" />
              )}
            </Button>

            <Box sx={{ mt: 2, textAlign: "center" }}>
              <Button
                type="button"
                onClick={handleBackToLogin}
                disabled={isFetching}
                style={{ width: "100%" }}
              >
                <FormattedMessage id="forgotpassword.button.backtologin" />
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}
